import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, User, ArrowLeft, Paperclip, X, Download, ZoomIn } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  message_body: string;
  created_at: string;
  status: string;
  media_urls?: string[];
  media_count?: number;
  has_media?: boolean;
}

interface Conversation {
  customer_phone: string;
  customer_name: string | null;
}

interface PhoneNumber {
  twilio_phone_number: string;
  display_name: string;
}

interface ChatInterfaceProps {
  conversationId: string;
  phoneNumberId: string;
}

export function ChatInterface({ conversationId, phoneNumberId }: ChatInterfaceProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [viewerImage, setViewerImage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConversation();
    loadPhoneNumber();
    loadMessages();
    markAsRead();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('messages-updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'sms_messages',
        filter: `conversation_id=eq.${conversationId}`
      }, (payload) => {
        setMessages((prev) => [...prev, payload.new as Message]);
        scrollToBottom();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initial scroll positioning on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const loadConversation = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_conversations')
        .select('customer_phone, customer_name')
        .eq('id', conversationId)
        .single();

      if (error) throw error;
      setConversation(data);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadPhoneNumber = async () => {
    try {
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('twilio_phone_number, display_name')
        .eq('id', phoneNumberId)
        .single();

      if (error) throw error;
      setPhoneNumber(data);
    } catch (error) {
      console.error('Error loading phone number:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages((data || []) as Message[]);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await supabase
        .from('sms_conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedImages.length === 0) || sending) return;

    setSending(true);
    setUploading(true);
    
    try {
      let mediaUrls: string[] = [];

      // Upload images to Supabase Storage if present
      if (selectedImages.length > 0) {
        for (const file of selectedImages) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${conversationId}/${Date.now()}-${Math.random()}.${fileExt}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('sms-media')
            .upload(fileName, file, {
              contentType: file.type,
              upsert: false
            });

          if (uploadError) throw uploadError;

          // Get signed URL
          const { data: signedUrlData } = await supabase.storage
            .from('sms-media')
            .createSignedUrl(fileName, 31536000); // 1 year

          if (signedUrlData?.signedUrl) {
            mediaUrls.push(signedUrlData.signedUrl);
          }
        }
      }

      const { error } = await supabase.functions.invoke('send-sms', {
        body: {
          conversationId,
          messageBody: newMessage.trim() || ' ',
          mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
        },
      });

      if (error) throw error;

      setNewMessage("");
      setSelectedImages([]);
      toast.success("Message sent");
      
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB max
      
      if (!isValidType) {
        toast.error(`${file.name}: Only JPG, PNG, and GIF images are supported`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name}: File size must be under 5MB`);
        return false;
      }
      return true;
    });

    if (selectedImages.length + validFiles.length > 10) {
      toast.error("Maximum 10 images per message");
      return;
    }

    setSelectedImages(prev => [...prev, ...validFiles]);
    if (e.target) e.target.value = '';
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      // Refocus immediately after Enter key send
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  const displayName = conversation?.customer_name || conversation?.customer_phone || 'Loading...';
  const formattedCustomerPhone = conversation?.customer_phone?.replace(
    /^\+?1?(\d{3})(\d{3})(\d{4})$/,
    '($1) $2-$3'
  );

  return (
    <div className="flex flex-col h-[100dvh]">
      {/* Compact Header with Back Button */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10 pt-safe">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/admin/sms-phone-numbers/${phoneNumberId}/conversations`)}
          className="flex-shrink-0 min-w-[44px] min-h-[44px]"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 flex-shrink-0">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-base truncate">{displayName}</h2>
            {conversation?.customer_name && formattedCustomerPhone && (
              <p className="text-sm text-muted-foreground truncate">{formattedCustomerPhone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3" ref={scrollRef}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No messages yet. Start the conversation!
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                    message.direction === 'outbound'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted text-foreground rounded-bl-md'
                  }`}
                >
                  {message.media_urls && message.media_urls.length > 0 && (
                    <div className={`grid gap-2 mb-2 ${message.media_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {message.media_urls.map((url, imgIdx) => (
                        <div key={imgIdx} className="relative group">
                          <img 
                            src={url} 
                            alt={`Attachment ${imgIdx + 1}`}
                            className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ maxHeight: '300px', objectFit: 'cover' }}
                            onClick={() => setViewerImage(url)}
                            loading="lazy"
                          />
                          <Button
                            size="icon"
                            variant="secondary"
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewerImage(url);
                            }}
                          >
                            <ZoomIn className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {message.message_body && (
                    <p className="whitespace-pre-wrap break-words text-base leading-relaxed">{message.message_body}</p>
                  )}
                  <p className={`text-sm mt-1.5 ${
                    message.direction === 'outbound'
                      ? 'text-primary-foreground/70'
                      : 'text-muted-foreground'
                  }`}>
                    {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Input - Sticky to Bottom with Safe Area */}
      <div className="px-4 py-2 pb-[calc(env(safe-area-inset-bottom)-15px)] border-t bg-background sticky bottom-0">
        {/* Image Previews */}
        {selectedImages.length > 0 && (
          <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
            {selectedImages.map((file, idx) => (
              <div key={idx} className="relative flex-shrink-0">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Preview ${idx + 1}`}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                  onClick={() => removeImage(idx)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 items-end">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploading}
            className="flex-shrink-0 min-w-[44px] min-h-[44px]"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
          
          <Textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={scrollToBottom}
            onBlur={(e) => {
              const relatedTarget = e.relatedTarget as HTMLElement;
              if (relatedTarget?.getAttribute('data-send-button') === 'true') {
                return;
              }
            }}
            placeholder={selectedImages.length > 0 ? "Add a caption (optional)..." : "Type a message..."}
            className="resize-none text-base min-h-[44px] max-h-32"
            rows={1}
            disabled={sending}
            inputMode="text"
            style={{ height: 'auto' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
            }}
          />
          
          <Button
            onMouseDown={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            data-send-button="true"
            disabled={(! newMessage.trim() && selectedImages.length === 0) || sending}
            size="icon"
            className="flex-shrink-0 min-w-[52px] min-h-[52px] rounded-full"
          >
            {sending || uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewerImage} onOpenChange={() => setViewerImage(null)}>
        <DialogContent className="max-w-4xl w-full p-0">
          <div className="relative">
            {viewerImage && (
              <>
                <img 
                  src={viewerImage} 
                  alt="Full size" 
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = viewerImage;
                      link.download = 'image.jpg';
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
